import React from 'react';
import { Calendar, MapPin, Plus, Circle, Loader2, Clock, Archive, MessageSquare, User } from 'lucide-react';
import Button from '../../../components/common/Button';
import Badge from '../../../components/common/Badge';
import Skeleton from '../../../components/common/Skeleton';
import NoDataFound from '../../../components/common/NoDataFound';
import { statusVariant } from '../../../utils/statusVariant';
import { useEntitySettings } from '../../../contexts/EntitySettingsContext';
import '../../../components/layout/ModuleLayout.css';

export interface ApiTask {
  id: number;
  name: string;
  description?: string;
  status: ApiTaskStatus;
  priority?: TaskPriority;
  due_date?: string;
  grace_period_days?: number;
  my_role?: string;
  linked_entities?: any[];
  linked_type_code?: string;
  linked_label?: string;
  created_by_name?: string | null;
  created_on?: string | null;
}

export type ApiTaskStatus = 'OPEN' | 'IN_PROGRESS' | 'PENDING' | 'DONE' | 'CANCELLED'
    | 'VERIFIED' | 'ARCHIVED' | 'REVERTED' | 'REJECTED'; // legacy back-compat
export type TaskPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

interface WorkTasksBodyProps {
  tasks: any[];
  activeTasks: any[];
  completedTasks: any[];
  loading: boolean;
  error: string | null;
  onSelectTask: (task: any) => void;
  onCreateClick: () => void;
  /** 'active' (default) = Action Centre, unchanged. 'archive' = History: the
   *  user's completed/closed tasks only, no create button. */
  scope?: 'active' | 'archive';
  /** When true, hide every mutating control (e.g. Create Task). Row click still fires. */
  readOnly?: boolean;
}

interface BucketDef {
  key: string;
  label: string;
  icon: React.ReactNode;
  accent: string;
  emptyText: string;
  filter: (t: any) => boolean;
}

// 3-column kanban for the Action Centre — Done tasks live in History only.
const BUCKETS: BucketDef[] = [
  {
    key: 'open',
    label: 'Open',
    icon: <Circle size={13} />,
    accent: '#f59e0b',
    emptyText: 'No open tasks.',
    // REVERTED = legacy "pushed-back" status — treated as Open so doer can re-submit
    filter: t => t.status === 'OPEN' || t.status === 'REVERTED',
  },
  {
    key: 'in_progress',
    label: 'In Progress',
    icon: <Loader2 size={13} />,
    accent: '#0284c7',
    // IN_PROGRESS = doer submitted, awaiting approver — visible for BOTH roles.
    emptyText: 'Nothing in progress.',
    filter: t => t.status === 'IN_PROGRESS',
  },
  {
    key: 'pending',
    label: 'Pending Ratification',
    icon: <Clock size={13} />,
    accent: '#8b5cf6',
    // PENDING = approved, awaiting ratifier. VERIFIED = legacy equivalent.
    emptyText: 'No tasks pending ratification.',
    filter: t => t.status === 'PENDING' || t.status === 'VERIFIED',
  },
];

const BucketSkeleton: React.FC = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '8px 4px' }}>
    {[1, 2, 3].map(i => (
      <div key={i} className="action-card action-card--skeleton">
        <div className="action-card__skel-header">
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Skeleton.Box width="40%" height={9} />
            <Skeleton.Box width="70%" height={12} />
            <Skeleton.Box width="55%" height={10} />
          </div>
          <Skeleton.Pill width={56} height={20} />
        </div>
        <div className="action-card__skel-meta">
          <Skeleton.Box width={80} height={10} />
          <Skeleton.Box width={100} height={10} />
        </div>
      </div>
    ))}
  </div>
);

// Pill shown in the In Progress bucket to distinguish the viewer's standing.
// DOER sees "Submitted" (they acted, waiting on approver).
// APPROVER sees "To Approve" (they need to act).
const InProgressRolePill: React.FC<{ role?: string }> = ({ role }) => {
  if (!role) return null;
  if (role === 'DOER') {
    return (
      <span style={{
        fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
        padding: '2px 7px', borderRadius: 99,
        background: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7',
        whiteSpace: 'nowrap',
      }}>Submitted</span>
    );
  }
  if (role === 'APPROVER') {
    return (
      <span style={{
        fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
        padding: '2px 7px', borderRadius: 99,
        background: '#dbeafe', color: '#1e40af', border: '1px solid #93c5fd',
        whiteSpace: 'nowrap',
      }}>To Approve</span>
    );
  }
  return null;
};

interface TaskCardProps {
  task: any;
  bucketKey: string;
  onSelectTask: (task: any) => void;
}

// Extract the latest note's text for a one-line card hint.
const getNotePreview = (notes: string): string => {
  if (!notes?.trim()) return '';
  const entries = notes.split('\n\n---\n\n');
  const latest = entries[entries.length - 1].trim();
  const lines = latest.split('\n');
  const text = lines[0].match(/^\[.+\]$/) ? lines.slice(1).join(' ').trim() : latest;
  const flat = text.replace(/\n/g, ' ');
  return flat.length > 38 ? flat.substring(0, 38) + '…' : flat;
};

const TaskCard: React.FC<TaskCardProps> = ({ task: t, bucketKey, onSelectTask }) => {
  const { formatDate } = useEntitySettings();
  const links = t.linked_entities || [];
  const fallbackLink = !links.length && t.linked_type_code && t.linked_label
    ? [{ id: -1, type_code: t.linked_type_code, label: t.linked_label }]
    : [];
  const allLinks = [...links, ...fallbackLink];
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const isOverdue = t.due_date && new Date(t.due_date) < today
    && !['DONE', 'ARCHIVED', 'VERIFIED', 'REJECTED', 'CANCELLED'].includes(t.status);
  const notePreview = getNotePreview(t.revert_notes || '');
  // Rejection sends a task back to OPEN/REVERTED with the rejection remark
  // appended to revert_notes (role tagged "(Rejected)"). Surface that so a
  // bounced-back task isn't mistaken for a fresh OPEN one.
  const wasRejected = (t.status === 'OPEN' || t.status === 'REVERTED')
    && /\(Rejected\)/i.test(t.revert_notes || '');

  return (
    <div
      key={t.id}
      data-testid={`BTN_work_tasks_row_${t.id}_open`}
      className="action-card"
      role="button"
      tabIndex={0}
      onClick={() => onSelectTask(t)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectTask(t); }
      }}
    >
      <div className="action-card__header">
        <div className="action-card__title-block">
          {t.my_role && (
            <span className="action-card__subtitle" style={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 'var(--font-weight-heading)' }}>
              {t.my_role.replace(/_/g, ' ')}
            </span>
          )}
          <span className="action-card__title">{t.name}</span>
          {t.description && (
            <span className="action-card__subtitle">{t.description}</span>
          )}
        </div>
        {/* In-progress bucket: swap status badge for the role action pill.
            A bounced-back (rejected) task shows a red Rejected badge instead
            of the plain OPEN one so the doer knows it needs rework. */}
        <div className="action-card__badge">
          {bucketKey === 'in_progress'
            ? <InProgressRolePill role={t.my_role} />
            : wasRejected
              ? <Badge variant="danger" size="xs">Rejected</Badge>
              : <Badge variant={statusVariant(t.status)} size="xs">{t.status.replace('_', ' ')}</Badge>
          }
        </div>
      </div>

      {(t.due_date || allLinks.length > 0 || notePreview || t.created_by_name || t.created_on) && (
        <div className="action-card__meta">
          {t.due_date && (
            <span className={`action-card__meta-item${isOverdue ? ' action-card__meta-item--overdue' : ''}`}>
              <Calendar size={11} />
              {formatDate(t.due_date)}
              {t.grace_period_days > 0 && <span>+{t.grace_period_days}d</span>}
            </span>
          )}
          {(t.created_by_name || t.created_on) && (
            <span className="action-card__meta-item" style={{ flex: '1 1 100%' }}>
              {t.created_by_name && (
                <>
                  <User size={11} />
                  <span style={{ fontWeight: 600 }}>Created by:</span>&nbsp;{t.created_by_name}
                </>
              )}
              {t.created_on && (
                <>
                  <Clock size={11} style={{ marginLeft: t.created_by_name ? 10 : 0 }} />
                  <span style={{ fontWeight: 600 }}>Created on:</span>&nbsp;{formatDate(t.created_on)}
                </>
              )}
            </span>
          )}
          {allLinks.length > 0 && (
            <span className="action-card__meta-item">
              <MapPin size={11} />
              {allLinks[0].type_code && <span className="status-badge xs neutral">{allLinks[0].type_code}</span>}
              <span style={{ maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{allLinks[0].label}</span>
              {allLinks.length > 1 && <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>+{allLinks.length - 1}</span>}
            </span>
          )}
          {notePreview && (
            <span className="action-card__meta-item" style={{ color: '#9ca3af', fontStyle: 'italic', flex: '1 1 100%' }}>
              <MessageSquare size={11} style={{ flexShrink: 0 }} />
              {notePreview}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// ── History table — flat list of all completed tasks ─────────────────────────
const HistoryTable: React.FC<{ tasks: any[]; loading: boolean; onSelectTask: (t: any) => void }> = ({
  tasks, loading, onSelectTask,
}) => {
  const { formatDate } = useEntitySettings();
  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '16px' }}>
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <Skeleton.Box width="12%" height={14} />
            <Skeleton.Box width="30%" height={14} />
            <Skeleton.Box width="10%" height={14} />
            <Skeleton.Box width="14%" height={14} />
            <Skeleton.Box width="20%" height={14} />
          </div>
        ))}
      </div>
    );
  }
  if (!tasks.length) {
    return <NoDataFound title="No History" message="No completed tasks yet." />;
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: '2px solid var(--border-color, #e5e7eb)', textAlign: 'left' }}>
            {['Status', 'Task', 'My Role', 'Due Date', 'Linked To', 'Last Remarks'].map(h => (
              <th key={h} style={{ padding: '8px 12px', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted, #6b7280)', whiteSpace: 'nowrap' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tasks.map(t => {
            const links = t.linked_entities || [];
            const fallbackLink = !links.length && t.linked_type_code && t.linked_label
              ? [{ type_code: t.linked_type_code, label: t.linked_label }] : [];
            const allLinks = [...links, ...fallbackLink];
            return (
              <tr
                key={t.id}
                data-testid={`BTN_work_tasks_row_${t.id}_open`}
                onClick={() => onSelectTask(t)}
                style={{ borderBottom: '1px solid var(--border-color, #f3f4f6)', cursor: 'pointer', transition: 'background 0.1s' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--hover-bg, #f9fafb)')}
                onMouseLeave={e => (e.currentTarget.style.background = '')}
              >
                <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                  <Badge variant={statusVariant(t.status)} size="xs">{t.status.replace('_', ' ')}</Badge>
                </td>
                <td style={{ padding: '10px 12px', maxWidth: 260 }}>
                  <div style={{ fontWeight: 600, color: 'var(--text-primary, #111827)' }}>{t.name}</div>
                  {t.description && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted, #6b7280)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240 }}>{t.description}</div>
                  )}
                </td>
                <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                  {t.my_role && (
                    <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-secondary, #374151)' }}>
                      {t.my_role.replace(/_/g, ' ')}
                    </span>
                  )}
                </td>
                <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: 'var(--text-secondary, #374151)', fontSize: 12 }}>
                  {t.due_date ? formatDate(t.due_date) : '—'}
                </td>
                <td style={{ padding: '10px 12px', maxWidth: 200 }}>
                  {allLinks.length > 0 ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      {allLinks[0].type_code && <span className="status-badge xs neutral">{allLinks[0].type_code}</span>}
                      <span style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }}>{allLinks[0].label}</span>
                      {allLinks.length > 1 && <span style={{ fontSize: 11, color: 'var(--text-muted, #6b7280)', fontWeight: 600 }}>+{allLinks.length - 1}</span>}
                    </span>
                  ) : '—'}
                </td>
                <td style={{ padding: '10px 12px', maxWidth: 200 }}>
                  {t.revert_notes ? (
                    <span style={{ display: 'flex', alignItems: 'flex-start', gap: 4, color: 'var(--text-secondary, #374151)', fontSize: 12 }}>
                      <MessageSquare size={12} style={{ marginTop: 1, flexShrink: 0, color: '#6b7280' }} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>{t.revert_notes}</span>
                    </span>
                  ) : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const WorkTasksBody: React.FC<WorkTasksBodyProps> = ({
  tasks,
  activeTasks,
  completedTasks,
  loading,
  onSelectTask,
  onCreateClick,
  scope = 'active',
  readOnly = false,
}) => {
  const isArchive = scope === 'archive';
  const showCreate = !isArchive && !readOnly;

  // History: flat table of all completed tasks.
  if (isArchive) {
    return (
      <div className="netbody-card flex flex-col h-full">
        <div className="netbody-content">
          <div className="scroll-container-nofilters">
            <HistoryTable tasks={completedTasks} loading={loading} onSelectTask={onSelectTask} />
          </div>
        </div>
      </div>
    );
  }

  // Action Centre: 3-column kanban (Open / In Progress / Pending Ratification).
  const emptyMessage = 'No tasks assigned to you yet. Click "Create Task" to add one.';

  return (
    <div className="netbody-card flex flex-col h-full">
      <div className="netbody-header">
        <div className="flex items-center gap-2 ml-auto">
          {showCreate && (
            <Button data-testid="BTN_work_tasks_create" variant="fill" icon={Plus} onClick={onCreateClick}>
              Create Task
            </Button>
          )}
        </div>
      </div>

      <div className="netbody-content">
        <div className="scroll-container">
          {!loading && activeTasks.length === 0 ? (
            <NoDataFound title="No Tasks Found" message={emptyMessage} />
          ) : (
            <div className="bucket-grid bucket-grid--3 pt-2">
              {BUCKETS.map(bucket => {
                const items = loading ? [] : activeTasks.filter(bucket.filter);
                return (
                  <div
                    key={bucket.key}
                    className="bucket-panel"
                    style={{ '--accent': bucket.accent } as React.CSSProperties}
                  >
                    <div className="bucket-panel-header">
                      <span className="bucket-panel-icon">{bucket.icon}</span>
                      <span className="bucket-panel-title">{bucket.label.toUpperCase()}</span>
                      <span className="bucket-panel-count">{loading ? '…' : items.length}</span>
                    </div>

                    <div className="bucket-panel-scroll">
                      {loading ? (
                        <BucketSkeleton />
                      ) : items.length === 0 ? (
                        <div className="bucket-empty-state">
                          <Archive size={28} className="bucket-empty-icon" />
                          <span className="bucket-empty-text">{bucket.emptyText}</span>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '8px 4px' }}>
                          {items.map(t => (
                            <TaskCard key={t.id} task={t} bucketKey={bucket.key} onSelectTask={onSelectTask} />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkTasksBody;
